import { X, Megaphone, Info, CheckCircle2, AlertTriangle, Sparkles, ExternalLink } from 'lucide-react';
import { useAnnouncements, useDismissAnnouncement, type Announcement } from '@/hooks/useAnnouncements';
import { formatDate } from '@/lib/utils';
import { cn } from '@/lib/utils';

const severityStyles: Record<Announcement['severity'], { wrapper: string; icon: React.ComponentType<{ className?: string }>; iconColor: string }> = {
  info: {
    wrapper: 'border-blue-200 bg-blue-50/60 dark:border-blue-900/40 dark:bg-blue-950/30',
    icon: Info,
    iconColor: 'text-blue-600 dark:text-blue-400',
  },
  success: {
    wrapper: 'border-emerald-200 bg-emerald-50/60 dark:border-emerald-900/40 dark:bg-emerald-950/30',
    icon: CheckCircle2,
    iconColor: 'text-emerald-600 dark:text-emerald-400',
  },
  warning: {
    wrapper: 'border-amber-200 bg-amber-50/60 dark:border-amber-900/40 dark:bg-amber-950/30',
    icon: AlertTriangle,
    iconColor: 'text-amber-600 dark:text-amber-400',
  },
  announcement: {
    wrapper: 'border-violet-200 bg-violet-50/60 dark:border-violet-900/40 dark:bg-violet-950/30',
    icon: Sparkles,
    iconColor: 'text-violet-600 dark:text-violet-400',
  },
};

export function AnnouncementsPanel() {
  const { data, isPending } = useAnnouncements();
  const dismiss = useDismissAnnouncement();

  if (isPending) {
    return (
      <aside className="w-full space-y-3">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-muted-foreground">
          <Megaphone className="w-4 h-4" /> Duyurular
        </h2>
        <div className="h-32 rounded-lg border bg-muted/30 animate-pulse" />
      </aside>
    );
  }

  const announcements = data?.announcements ?? [];

  return (
    <aside className="w-full space-y-3">
      <h2 className="flex items-center gap-2 text-sm font-semibold text-muted-foreground">
        <Megaphone className="w-4 h-4" /> Duyurular
      </h2>
      {announcements.length === 0 ? (
        <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
          Şu an aktif bir duyuru yok.
        </div>
      ) : (
        <div className="space-y-3">
          {announcements.map((a) => {
            const style = severityStyles[a.severity] ?? severityStyles.info;
            const Icon = style.icon;
            return (
              <article
                key={a.id}
                className={cn('relative rounded-lg border p-4 pr-10 text-sm shadow-sm', style.wrapper)}
              >
                {a.dismissible && (
                  <button
                    type="button"
                    onClick={() => dismiss.mutate(a.id)}
                    aria-label="Duyuruyu kapat"
                    className="absolute top-2 right-2 p-1 rounded-md hover:bg-background/60 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
                <div className="flex items-start gap-2 mb-1.5">
                  <Icon className={cn('w-4 h-4 mt-0.5 shrink-0', style.iconColor)} />
                  <h3 className="font-semibold leading-snug">{a.title}</h3>
                </div>
                {a.body_html && (
                  <div
                    className="prose prose-sm max-w-none text-foreground/80 [&_p]:my-1 [&_strong]:font-semibold"
                    dangerouslySetInnerHTML={{ __html: a.body_html }}
                  />
                )}
                <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
                  <span>{formatDate(a.published_at)}</span>
                  {a.link_url && a.link_label && (
                    <a
                      href={a.link_url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-primary hover:underline"
                    >
                      {a.link_label} <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </aside>
  );
}
